import React, { useState } from 'react';
import InlineLayout from '@/shared/layout/inlineLayout';
import Loader from '@/app/routes/Loader';
import ErrorPage403 from '@/app/routes/errorPage403';
import { Snackbar } from '@/shared/components/Snackbar';
import { Tooltip } from '@/shared/components/Tooltip';
import { useUserPrivileges } from '@/shared/hooks/useUserPrivileges';
import {
  useViewContactRequest,
  getContactRequestStatusConfig,
  formatContactRequestDate,
} from '../../hooks/useViewContactRequest';
import { useUpdateContactRequestStatus } from '../../hooks/useUpdateContactRequestStatus';
import type {
  AdminNote,
  ViewContactRequestData,
} from '../../api/viewContactRequestApiService';
import {
  type ContactRequestStatus,
  TERMINAL_STATUSES,
} from '../../types/contactRequestTypes';
import { CONTACT_REQUEST_LABELS as L } from '../../constants/labels';
import CancelContactRequestModal from './CancelContactRequestModal';
import UpdateStatusModal from './UpdateStatusModal';

const PLACEHOLDER = '—';

/** Read-only label+value pair used throughout the basic info and address sections. */
function InfoField({
  label,
  value,
  fullWidth = false,
  testId,
}: {
  label: string;
  value: string | null | undefined;
  fullWidth?: boolean;
  testId?: string;
}) {
  return (
    <div
      className={`flex min-w-0 flex-col gap-2${fullWidth ? ' col-span-full' : ''}`}
      {...(testId ? { 'data-testid': testId } : {})}
    >
      <h2 className="text-bodySmall font-normal text-on-surface-variant">
        {label}
      </h2>
      <p
        className="min-w-0 wrap-break-word text-bodyLarge font-normal text-neutral-10"
        {...(testId ? { 'data-testid': `${testId}-value` } : {})}
      >
        {value || PLACEHOLDER}
      </p>
    </div>
  );
}

/** Coloured pill badge showing the human-readable label for a contact request status. */
function StatusBadge({ status }: { status: string }) {
  const cfg = getContactRequestStatusConfig(status);
  return (
    <span
      className="inline-flex items-center gap-2 px-3 py-1 rounded-lg whitespace-nowrap"
      style={{
        backgroundColor: cfg.bg,
        color: cfg.text,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '20px',
        letterSpacing: '0.1px',
      }}
      data-testid={`view-cr-status-${status}`}
    >
      <span
        className="rounded-full shrink-0"
        style={{ width: 6, height: 6, backgroundColor: cfg.text }}
      />
      {cfg.label}
    </span>
  );
}

/** "Podstawowe informacje" tab — client details, subject, message, and address. */
function BasicInfoTab({ data }: { data: ViewContactRequestData }) {
  return (
    <div className="flex flex-col gap-6 m-1.5">
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-lg p-4">
        <p
          className="text-[18px] font-medium text-on-surface-variant"
          style={{ lineHeight: '26px' }}
        >
          {L.VIEW_CR_SECTION_BASIC_DATA}
        </p>
        <div className="grid grid-cols-2 gap-x-8 gap-y-8">
          <InfoField
            label={L.VIEW_CR_FIELD_CLIENT_ID}
            value={data.client_user_id}
            testId="view-cr-field-client-id"
          />
          <InfoField
            label={L.VIEW_CR_FIELD_CLIENT_NAME}
            value={data.client_name}
            testId="view-cr-field-client-name"
          />
          <InfoField
            label={L.VIEW_CR_FIELD_EMAIL}
            value={data.client_email}
            testId="view-cr-field-email"
          />
          <InfoField label={L.VIEW_CR_FIELD_PHONE} value={data.client_phone} />
          <InfoField
            label={L.VIEW_CR_FIELD_CREATED_AT}
            value={formatContactRequestDate(data.created_at)}
          />
          <InfoField
            label={L.VIEW_CR_FIELD_SUBJECT}
            value={data.subject}
            testId="view-cr-field-subject"
          />
          <InfoField
            label={L.VIEW_CR_FIELD_MESSAGE}
            value={data.message}
            fullWidth
          />
        </div>
      </div>

      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-lg p-4">
        <p
          className="text-[18px] font-medium text-on-surface-variant"
          style={{ lineHeight: '26px' }}
        >
          {L.VIEW_CR_SECTION_ADDRESS}
        </p>
        <div className="grid grid-cols-2 gap-x-8 gap-y-8">
          <InfoField
            label={L.VIEW_CR_FIELD_ADDRESS_LINE1}
            value={data.client_address_line1}
          />
          <InfoField
            label={L.VIEW_CR_FIELD_ADDRESS_LINE2}
            value={data.client_address_line2}
          />
          <InfoField label={L.VIEW_CR_FIELD_CITY} value={data.client_city} />
          <InfoField label={L.VIEW_CR_FIELD_STATE} value={data.client_state} />
          <InfoField label={L.VIEW_CR_FIELD_ZIP} value={data.client_zip_code} />
          <InfoField
            label={L.VIEW_CR_FIELD_COUNTRY}
            value={data.client_country}
          />
        </div>
      </div>
    </div>
  );
}

/** Single timestamped admin note entry rendered inside the notes list. */
function AdminNoteEntry({ note }: { note: AdminNote }) {
  return (
    <div className="flex flex-col gap-2 w-full py-4">
      <p className="text-[14px] font-normal leading-5 tracking-[0.25px] text-on-surface-variant wrap-break-word whitespace-pre-wrap">
        {note.note}
      </p>
      <p className="text-[12px] font-normal leading-4 tracking-[0.4px] text-on-surface-variant">
        {formatContactRequestDate(note.created_at)}
      </p>
    </div>
  );
}

/** "Notatki administratora" tab — lists all admin notes or an empty state. */
function AdminNotesTab({ adminNotes }: { adminNotes: AdminNote[] }) {
  return (
    <div
      className="flex flex-col gap-4 border border-[#DCDDE0] rounded-lg p-4 m-1.5 flex-1"
      data-testid="view-cr-admin-notes-section"
    >
      <p className="text-[18px] font-medium leading-6.5 text-on-surface-variant">
        {L.VIEW_CR_SECTION_ADMIN_NOTES}
      </p>
      {adminNotes.length > 0 ? (
        <div className="flex flex-col">
          {adminNotes.map((n, i) => (
            <AdminNoteEntry key={i} note={n} />
          ))}
        </div>
      ) : (
        <p className="px-5 py-2 text-center text-[14px] font-normal leading-5 tracking-[0.25px] text-on-surface-variant">
          {L.VIEW_CR_NOTES_EMPTY}
        </p>
      )}
    </div>
  );
}

/** Full-page error screen shown when the contact request cannot be loaded. */
function ViewContactRequestErrorScreen() {
  return (
    <div className="flex items-center justify-center h-full">
      <p className="text-on-surface-variant text-bodyLarge">
        {L.VIEW_CR_ERROR_LOAD}
      </p>
    </div>
  );
}

const NAV_ITEMS = [
  { id: 'podstawowe', label: L.VIEW_CR_NAV_BASIC_INFO },
  { id: 'notatki', label: L.VIEW_CR_NAV_ADMIN_NOTES },
] as const;

/**
 * Detail page for a single contact request.
 *
 * Fetches data via `useViewContactRequest`, exposes a status-update flow
 * through `useUpdateContactRequestStatus`, and shows a success snackbar
 * after a successful transition. The "Zmiana statusu" button is hidden
 * once the request reaches a terminal status (completed / cancelled).
 *
 * @param contactRequestId - UUID of the contact request (from the route param)
 */
const ViewContactRequest: React.FC<{ contactRequestId: string }> = ({
  contactRequestId,
}) => {
  const { data, isLoading, showErrorScreen, accessError, refetch } =
    useViewContactRequest(contactRequestId);
  const { privileges, isLoading: isPrivilegesLoading } = useUserPrivileges();

  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>(
    L.VIEW_CR_SUCCESS_STATUS_UPDATED
  );

  const modal = useUpdateContactRequestStatus({
    contactRequestId,
    currentStatus: data?.status as ContactRequestStatus | undefined,
    onSuccess: () => {
      setSuccessMessage(L.VIEW_CR_SUCCESS_STATUS_UPDATED);
      setShowSuccessSnackbar(true);
      refetch();
    },
    onCancelSuccess: () => {
      setSuccessMessage(L.VIEW_CR_SUCCESS_CANCELLED);
      setShowSuccessSnackbar(true);
      refetch();
    },
  });

  if (!isPrivilegesLoading && !privileges.contactRequestView) {
    return <ErrorPage403 />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  if (accessError) {
    return <ErrorPage403 />;
  }

  if (showErrorScreen || !data) {
    return <ViewContactRequestErrorScreen />;
  }

  const isTerminal = TERMINAL_STATUSES.includes(
    data.status as ContactRequestStatus
  );

  const header = (
    <div className="flex items-center justify-between w-full gap-4 flex-wrap">
      <div className="flex items-center gap-3">
        <p
          className="font-medium text-neutral-10"
          style={{ fontSize: '18px', lineHeight: '28px' }}
          data-testid="view-cr-request-id"
        >
          {data.contact_request_id}
        </p>
        <StatusBadge status={data.status} />
      </div>
      {!isTerminal &&
        (privileges.contactRequestUpdateStatus ? (
          <button
            type="button"
            onClick={modal.open}
            className="px-4 py-2.5 rounded-lg border border-[#DCDDE0] text-[#474A4C] text-sm font-medium leading-5 tracking-[0.1px] hover:bg-[#F2F4F5] transition-colors cursor-pointer shrink-0"
            data-testid="open-update-cr-status-btn"
          >
            {L.VIEW_CR_BUTTON_UPDATE_STATUS}
          </button>
        ) : (
          <Tooltip
            content="Nie masz uprawnień do wykonania tej czynności"
            side="bottom"
            forceShow
            triggerWrapContent
          >
            <button
              type="button"
              disabled
              className="px-4 py-2.5 rounded-lg border border-[#DCDDE0] text-[#474A4C] text-sm font-medium leading-5 tracking-[0.1px] opacity-50 cursor-not-allowed shrink-0"
              data-testid="open-update-cr-status-btn"
            >
              {L.VIEW_CR_BUTTON_UPDATE_STATUS}
            </button>
          </Tooltip>
        ))}
    </div>
  );

  return (
    <React.Fragment key={contactRequestId}>
      <InlineLayout
        title={L.VIEW_CR_TITLE}
        navItems={[...NAV_ITEMS]}
        contentByNavId={{
          podstawowe: <BasicInfoTab data={data} />,
          notatki: <AdminNotesTab adminNotes={data.admin_notes} />,
        }}
        header={header}
        searchParamKey="tab"
        defaultActiveId="podstawowe"
      />

      <UpdateStatusModal
        open={modal.isOpen}
        onClose={modal.close}
        currentStatus={data.status as ContactRequestStatus}
        allowedStatuses={modal.allowedStatuses}
        newStatus={modal.newStatus}
        onNewStatusChange={modal.setNewStatus}
        newStatusError={modal.newStatusError}
        note={modal.note}
        onNoteChange={modal.setNote}
        noteError={modal.noteError}
        isSubmitting={modal.isSubmitting}
        submitError={modal.submitError}
        onSubmit={modal.submit}
        canSubmit={modal.canSubmit}
      />

      <CancelContactRequestModal
        open={modal.isCancelModalOpen}
        onBack={modal.closeCancelModal}
        clientName={data.client_name}
        clientEmail={data.client_email}
        cancelNote={modal.cancelNote}
        onCancelNoteChange={modal.setCancelNote}
        cancelNoteError={modal.cancelNoteError}
        isSubmitting={modal.isSubmitting}
        submitError={modal.cancelSubmitError}
        onSubmit={modal.submitCancellation}
      />

      <Snackbar
        open={showSuccessSnackbar}
        message={successMessage}
        variant="success"
        position="top-center"
        onClose={() => setShowSuccessSnackbar(false)}
      />
    </React.Fragment>
  );
};

export default ViewContactRequest;
